import { makeAutoObservable, runInAction } from "mobx";
import { db } from "@stores/environments/Database.web";
import { ref, get, set, update, child } from "firebase/database";
import { IBanner } from "@interfaces/ibanner";
import { logService } from "../services/LogService";

class BannerStore {
  banners: IBanner[] = [];
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  fetchBanners = async () => {
    this.loading = true;
    const dbRef = ref(db);

    try {
      const snapshot = await get(child(dbRef, "banners"));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const allBanners: IBanner[] = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        runInAction(() => {
          this.banners = allBanners.filter(banner => !banner.deleted);
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.banners = [];
          this.loading = false;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar banners:", error);
      runInAction(() => {
        this.error = "Erro ao carregar banners.";
        this.loading = false;
      });
    }
  };

  saveBanner = async (banner: IBanner) => {
    const dbRef = ref(db, `banners/${banner.id}`);
    
    // Busca o valor anterior para verificar se é update ou create
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }
    
    const isUpdate = !!oldValue;
    
    await set(dbRef, banner);
    
    // Registra o log
    const logData = isUpdate 
      ? logService.updateLogData(
          'Banner',
          banner.id,
          `banners/${banner.id}`,
          oldValue,
          banner,
          `Banner ${isUpdate ? 'atualizado' : 'criado'}: ${banner.title}`
        )
      : logService.createLogData(
          'Banner',
          banner.id,
          `banners/${banner.id}`,
          banner,
          `Banner criado: ${banner.title}`
        );
    
    await logService.logOperation(logData);
    
    this.fetchBanners(); // Recarrega a lista após salvar
  };

  deleteBanner = async (id: string) => {
    const confirmed = window.confirm("Tem certeza que deseja excluir este banner?");
    if (!confirmed) {
      return;
    }

    // Busca o valor anterior para o log
    const dbRef = ref(db, `banners/${id}`);
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }

    const deleteRef = ref(db, `banners/${id}/deleted`);
    await set(deleteRef, true);
    
    // Registra o log
    if (oldValue) {
      const logData = logService.deleteLogData(
        'Banner',
        id,
        `banners/${id}`,
        oldValue,
        `Banner excluído: ${oldValue.title}`
      );
      
      await logService.logOperation(logData);
    }
    
    this.fetchBanners(); // Recarrega a lista após deletar
  };

  toggleActive = async (id: string, isActive: boolean) => {
    const dbRef = ref(db, `banners/${id}`);
    
    // Busca o valor anterior para o log
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }
    
    await update(dbRef, { isActive });
    
    // Registra o log
    if (oldValue) {
      const newValue = { ...oldValue, isActive };
      const logData = logService.toggleLogData(
        'Banner',
        id,
        `banners/${id}`,
        oldValue,
        newValue,
        `Banner ${isActive ? 'ativado' : 'desativado'}: ${oldValue.title}`
      );
      
      await logService.logOperation(logData);
    }
    
    this.fetchBanners();
  };
}

export const bannerStore = new BannerStore();
